import React, { ReactNode, useCallback, useEffect, useReducer } from "react";
import { Layout } from "views/Main/Layouts";
import { Link } from "polyrouter";
import {
  Loader,
  LoadingIndicator,
  NoResultsIndicatorAnimation
} from "elements";
import { useTranslation } from "utilities/i18n";
import { handleNullFields } from "utilities/stringUtils";
import {
  getColumns,
  HEADER_ID,
  itemCheckedWithSet,
  sortTableData,
  TableBody,
  TableBulkItemHeader,
  TableBulkItemRow,
  TableHeaderItem,
  TableSearchEdit,
  toggleAll,
  toggleItemWithSet
} from "collections/Table";
import { TABLE_COLUMN_STATE, TABLES } from "store/userMeta";
import { useSelector } from "react-redux";
import { State } from "store";
import { generateTableHeaders } from "utilities/tableUtils";
import { getProductDetailsLink } from "../View/ProductCatalogDetails";
import {
  defaultProductCatalogState,
  ProductCatalogReducer,
  updatePageInfo,
  updateSearch,
  updateSelectedItems,
  updateSort
} from "./ProductCatalogListReducer";
import { SortState } from "store/filters";
import {
  ProductLatestVersion,
  useGetLatestProductVersionsList
} from "../useProductCatalog";

type ProductCatalogRowProps = {
  product: ProductLatestVersion;
  onCheck: (id: string) => void;
  checked: boolean;
  showChecks: boolean;
  shownColumns: Array<string>;
};

export const ProductCatalogRow: React.FC<ProductCatalogRowProps> = ({
  product,
  onCheck,
  checked,
  showChecks,
  shownColumns
}) => {
  const { productId, name, latestVersion } = product;
  const { COLUMNS } = TABLES.CLARITI_PRODUCT_VERSIONS;

  const getTableContent = (column: string) => {
    let content: ReactNode = null;
    switch (column) {
      case COLUMNS.NAME:
        content = (
          <Link to={getProductDetailsLink(productId)}>
            {handleNullFields(name)}
          </Link>
        );
        break;
      case COLUMNS.LATEST_VERSION:
        content = handleNullFields(latestVersion);
        break;
    }

    return <td key={column}>{content}</td>;
  };

  return (
    <TableBulkItemRow
      data-testid={product.name}
      checked={checked}
      onCheck={() => onCheck(productId)}
      showChecks={showChecks}
    >
      {shownColumns.map(getTableContent)}
    </TableBulkItemRow>
  );
};

export const ProductCatalogList: React.FC<{
  tenantId: string;
}> = ({ tenantId }) => {
  const { Content } = Layout;
  const [
    { selectedItems, sort, searchTerm, productCount, products },
    dispatch
  ] = useReducer(ProductCatalogReducer, defaultProductCatalogState);

  const { NAME: TABLE_NAME, COLUMNS } = TABLES.CLARITI_PRODUCT_VERSIONS;
  const {
    [TABLE_COLUMN_STATE.SHOWN]: shownColumns,
    [TABLE_COLUMN_STATE.HIDDEN]: hiddenColumns
  } = useSelector((state: State) => {
    return state.userMeta?.tables[TABLE_NAME] ?? {};
  });

  useEffect(() => {
    dispatch(updateSelectedItems([]));
  }, [tenantId]);

  const { data, fetching, stale } = useGetLatestProductVersionsList({
    // Product catalog not updated frequently, so no need to re-run queries when view is re-routed
    requestPolicy: "cache-first"
  });

  const latestProductVersionsData = data?.latestVersions;

  useEffect(() => {
    if (latestProductVersionsData && !stale && !fetching) {
      dispatch(
        updatePageInfo({
          productCount: latestProductVersionsData.length,
          products: latestProductVersionsData.map(entry => {
            return {
              ...entry
            };
          })
        })
      );
    }
  }, [latestProductVersionsData, stale, fetching]);

  const setSelectedItems = useCallback((items: Array<string>) => {
    dispatch(updateSelectedItems(items));
  }, []);
  const setSort = useCallback((sort: SortState) => {
    dispatch(updateSort(sort));
  }, []);
  const setSearch = useCallback((search?: string) => {
    dispatch(updateSearch(search));
  }, []);

  // Sort products
  sortTableData(products, sort);

  const toggleItem = toggleItemWithSet(
    products.length,
    selectedItems,
    setSelectedItems
  );
  const itemChecked = itemCheckedWithSet(selectedItems);

  const { t } = useTranslation();
  const columnTranslations = {
    [COLUMNS.NAME]: t("NAME"),
    [COLUMNS.LATEST_VERSION]: t("LATEST_VERSION")
  };

  const getTableHeaders = (column: string, title: string) => (
    <TableHeaderItem
      fieldName={column}
      title={title}
      sort={{
        currentSort: sort,
        onSort: setSort
      }}
      key={column}
    />
  );

  const shown = getColumns(shownColumns, columnTranslations);
  const hidden = getColumns(hiddenColumns, columnTranslations);

  const formattedSearchTerm = searchTerm?.toLowerCase() ?? undefined;

  const filteredData = !!formattedSearchTerm;

  const filteredProducts = formattedSearchTerm
    ? products.filter(product =>
        product?.name?.toLowerCase().includes(formattedSearchTerm)
      )
    : products;

  const displayCount = filteredData ? filteredProducts.length : productCount;

  return (
    <Content>
      <Loader loading={fetching}>
        {productCount === 0 ? (
          <NoResultsIndicatorAnimation title={t("NO_PRODUCTS_FOUND")} />
        ) : (
          <>
            <div className="table-heading">
              <span className="metric" data-testid="product-count">
                {displayCount}
              </span>
              <span className="title ml-5">
                {t("PRODUCT", { count: displayCount })}
              </span>
            </div>
            <div className="mb-15 table-actions">
              <TableSearchEdit
                search={{
                  onSearch: setSearch,
                  searchPlaceholder: t("SEARCH_PRODUCT_CATALOG")
                }}
                edit={{
                  tableName: TABLE_NAME,
                  shownColumns: shown,
                  hiddenColumns: hidden
                }}
              />
            </div>
            <div className="table-container">
              <table className="table custom-table">
                <TableBulkItemHeader
                  checked={itemChecked(HEADER_ID)}
                  onCheck={() =>
                    toggleAll(
                      products.map(product => product.productId),
                      selectedItems,
                      setSelectedItems
                    )
                  }
                  showChecks={!!shownColumns.length}
                >
                  {generateTableHeaders(shown, getTableHeaders)}
                </TableBulkItemHeader>
                <TableBody
                  loading={fetching}
                  cols={shown.length}
                  accommodateScrollbar
                >
                  {filteredProducts.map(product => {
                    return (
                      <ProductCatalogRow
                        product={product}
                        key={product.productId}
                        onCheck={() => toggleItem(product.productId)}
                        checked={itemChecked(product.productId)}
                        showChecks={!!shownColumns.length}
                        shownColumns={shown.map(column => column.key)}
                      />
                    );
                  })}
                  {stale && (
                    <LoadingIndicator className="inline" title={t("LOADING")} />
                  )}
                </TableBody>
              </table>
            </div>
          </>
        )}
      </Loader>
    </Content>
  );
};
